import React, { useRef, useState } from "react";
import { Row, Col, Badge } from "react-bootstrap";

const InfoItem = ({ label, value, fullWidth }) => (
  <Col md={fullWidth ? 12 : 6} className="mb-3">
    <div className="text-muted fs-14 mb-1">{label}</div>
    <div className="fw-bold text-black fs-13 text-truncate" title={value}>
      {value || "--"}
    </div>
  </Col>
);

const InfoCard = ({ title, icon, children }) => (
  <div className="card border-0 shadow-sm rounded-4 h-100 overflow-hidden">
    <div className="card-header bg-white border-0 pt-4 px-4 pb-0">
      <div className="d-flex align-items-center gap-2">
        {icon && <div className="text-primary fs-18">{icon}</div>}
        <h5 className="mb-0 fw-bold fs-16 text-black">{title}</h5>
      </div>
    </div>
    <div className="card-body p-4">{children}</div>
  </div>
);

const Step4ConfirmTransaction = ({
  selectedCustomer,
  selectedReceivers,
  formData,
  setFormData,
}) => {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(formData?.invoiceFile || null);
  const [preview, setPreview] = useState(null);

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      if (file.type.startsWith("image/")) {
        setPreview(URL.createObjectURL(file));
      } else {
        setPreview(null);
      }
      if (setFormData) {
        setFormData({ ...formData, invoiceFile: file });
      }
    }
  };

  const handleDeleteFile = (e) => {
    e.stopPropagation();
    setSelectedFile(null);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (setFormData) {
      setFormData({ ...formData, invoiceFile: null });
    }
  };

  // Mock data for receivers if none selected (for development/fallback)
  const firstReceiverId = selectedReceivers?.[0];
  const receiverMockData = [
    {
      id: "8397",
      name: "SparkMinds check duplicate name",
      contactNo: "+65 1231 231 2318",
      receiverType: "Individual",
      relationship: "Related company",
      bank: "ASIA UNITED BANK",
      correspondenceBank: "--",
      deliveryMethod: "Bank Transfer",
    },
    {
      id: "8377",
      name: "Thuynga Check level limit",
      contactNo: "+65 1234 567 8900",
      receiverType: "Organization",
      relationship: "Customer",
      bank: "OMNIPAY, INC.",
      correspondenceBank: "--",
      deliveryMethod: "Bank Transfer",
    },
    {
      id: "8375",
      name: "Thuy Nga Check limit",
      bank: "OMNIPAY, INC.",
      deliveryMethod: "Bank Transfer",
    },
  ];

  const receiver =
    receiverMockData.find((r) => r.id === firstReceiverId) ||
    receiverMockData[0];
  const customer = selectedCustomer || {
    name: "--",
    type: "--",
    idCard: "--",
    phone: "--",
  };

  const exchangeRate = 36.45;
  const sendAmount = parseFloat(formData?.transferAmount || 0);
  const fee = parseFloat(formData?.fee || 0);
  const totalPaid = sendAmount + fee;

  return (
    <div className="mt-4">
      <h4 className="fw-bold text-black mb-4">Transaction Summary</h4>
      <Row className="g-4">
        {/* SENDER INFO */}
        <Col lg={4}>
          <InfoCard
            title="Sender Information"
            icon={<i className="far fa-user"></i>}
          >
            <Row>
              <InfoItem label="Name" value={customer.name} fullWidth />
              <InfoItem label="Customer Type" value={customer.type} />
              <InfoItem label="ID Number" value={customer.idCard} />
              <InfoItem label="Phone Number" value={customer.phone} />
              <InfoItem label="Email" value={customer.email} />
            </Row>
          </InfoCard>
        </Col>

        {/* RECEIVER INFO */}
        <Col lg={4}>
          <InfoCard
            title="Receiver Information"
            icon={<i className="far fa-user"></i>}
          >
            <Row>
              <InfoItem label="Name" value={receiver.name} fullWidth />
              <InfoItem label="Bank Name" value={receiver.bank} />
              <InfoItem
                label="Receiving Method"
                value={receiver.deliveryMethod}
              />
              <InfoItem label="Source of Funds" value={formData?.source} />
              <InfoItem label="Purpose of Transfer" value={formData?.purpose} />
            </Row>
          </InfoCard>
        </Col>

        {/* PAYMENT SUMMARY */}
        <Col lg={4}>
          <InfoCard
            title="Payment Summary"
            icon={<i className="far fa-credit-card"></i>}
          >
            <div className="d-flex flex-column gap-3">
              <div className="d-flex justify-content-between">
                <span className="text-muted fs-14">Send Amount</span>
                <span className="fw-bold text-black fs-14">
                  {sendAmount.toLocaleString()}{" "}
                  {formData?.sendCurrency || "SGD"}
                </span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-muted fs-14">Receiving Amount</span>
                <span className="fw-bold text-black fs-14 text-primary">
                  {(sendAmount * exchangeRate).toLocaleString()} PHP
                </span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-muted fs-14">Exchange Rate</span>
                <span className="fw-medium text-secondary fs-14">
                  1 {formData?.sendCurrency || "SGD"} = {exchangeRate} PHP
                </span>
              </div>
              <div className="d-flex justify-content-between pb-3 border-bottom">
                <span className="text-muted fs-14">Service Charge</span>
                <span className="fw-bold text-black fs-14">
                  {fee.toLocaleString()} {formData?.sendCurrency || "SGD"}
                </span>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-2 pt-2">
                <span className="fw-bold fs-15 text-black">Paid Amount</span>
                <div className="text-end">
                  <span className="fw-bold fs-18 text-primary d-block">
                    {totalPaid.toLocaleString()}{" "}
                    {formData?.sendCurrency || "SGD"}
                  </span>
                </div>
              </div>
            </div>
          </InfoCard>
        </Col>
      </Row>

      {/* Upload Invoice Section */}
      <div className="mt-5">
        <h5 className="fw-bold text-black mb-3 fs-15">Upload invoice</h5>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          accept=".pdf,.jpg,.jpeg,.png,.docx"
          onChange={handleFileChange}
        />
        <div
          className="bg-white rounded-4 shadow-sm d-flex align-items-center justify-content-center py-4 cursor-pointer border border-2 border-dashed position-relative"
          style={{
            minHeight: "100px",
            borderStyle: "dashed !important",
            borderColor: selectedFile ? "var(--primary)" : "#e0e0e0",
            backgroundColor: selectedFile ? "rgba(var(--primary-rgb), 0.05)" : "white",
          }}
          onClick={handleUploadClick}
        >
          {selectedFile && (
            <div
              className="position-absolute"
              style={{ top: "10px", right: "20px", zIndex: 10 }}
              onClick={handleDeleteFile}
              title="Remove file"
            >
              <i className="fas fa-trash-alt text-danger fs-18"></i>
            </div>
          )}

          <div className="d-flex align-items-center gap-3">
            {preview ? (
              <img
                src={preview}
                alt="preview"
                style={{
                  width: "50px",
                  height: "50px",
                  objectFit: "cover",
                  borderRadius: "8px",
                  border: "1px solid #eee",
                }}
              />
            ) : (
              <i
                className={selectedFile ? "fas fa-file-invoice text-primary" : "fas fa-cloud-upload-alt"}
                style={{
                  fontSize: "40px",
                  color: selectedFile ? "var(--primary)" : "#D0D5DD",
                }}
              ></i>
            )}
            <div>
              <div className="fw-bold text-black fs-14 text-truncate" style={{ maxWidth: "250px" }}>
                {selectedFile ? selectedFile.name : "Upload file"}
              </div>
              <div className="text-muted fs-11">
                {selectedFile
                  ? `${(selectedFile.size / 1024 / 1024).toFixed(2)} MB`
                  : "pdf, jpg, png, docx less than 5MB"}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .card {
          transition: transform 0.2s ease-in-out;
        }
        .text-primary {
          color: var(--primary) !important;
        }
        .border-dashed {
           border-style: dashed !important;
        }
        .bg-light-blue-soft {
           background-color: #F0F7FF;
        }
      `}</style>
    </div>
  );
};

export default Step4ConfirmTransaction;
